import React, { useState, useEffect } from 'react';
import { 
  PlusIcon, 
  TrashIcon, 
  CheckIcon, 
  XMarkIcon,
  PencilIcon,
  HomeIcon,
  BuildingOfficeIcon,
  TruckIcon,
  ComputerDesktopIcon,
  BanknotesIcon,
  ChartBarIcon,
  CubeIcon,
  ArrowTrendingUpIcon,
  CalendarDaysIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  DocumentTextIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  EyeIcon,
  ClipboardDocumentListIcon,
  CalculatorIcon,
  ChartPieIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { createAsset, updateAsset, deleteAsset, getAssets } from '../services/cashFlowService';
import debtService from '../../../services/debtService';
import { Card, CardContent, Typography, Chip, Button, Collapse, Divider } from '@mui/material';
import AssetCreateModal from './AssetCreateModal';
import AssetsDesktopCards from './AssetsDesktopCards';

const ASSET_TYPES = [
  { value: 'real_estate', label: 'Real Estate', icon: HomeIcon, color: 'text-blue-600', taxCategory: 'capital' },
  { value: 'vehicle', label: 'Vehicle', icon: TruckIcon, color: 'text-green-600', taxCategory: 'personal' },
  { value: 'investment', label: 'Investment', icon: ChartBarIcon, color: 'text-purple-600', taxCategory: 'investment' },
  { value: 'business', label: 'Business Asset', icon: BuildingOfficeIcon, color: 'text-orange-600', taxCategory: 'business' },
  { value: 'electronics', label: 'Electronics', icon: ComputerDesktopIcon, color: 'text-indigo-600', taxCategory: 'personal' },
  { value: 'cash', label: 'Cash/Savings', icon: BanknotesIcon, color: 'text-emerald-600', taxCategory: 'liquid' },
  { value: 'personal', label: 'Personal Property', icon: CubeIcon, color: 'text-gray-600', taxCategory: 'personal' }
];

const ASSET_TYPE_ICONS = {
  real_estate: HomeIcon,
  vehicle: TruckIcon,
  investment: ChartBarIcon,
  business: BuildingOfficeIcon,
  electronics: ComputerDesktopIcon,
  cash: BanknotesIcon,
  personal: CubeIcon
};

const SAVINGS_TYPE_ICONS = {
  emergency: BanknotesIcon,
  vacation: HomeIcon,
  car: TruckIcon,
  house: HomeIcon,
  retirement: ChartBarIcon,
  general: BanknotesIcon
};

const CONDITION_OPTIONS = [
  { value: 'excellent', label: 'Excellent', factor: 1.0 },
  { value: 'good', label: 'Good', factor: 0.9 },
  { value: 'fair', label: 'Fair', factor: 0.75 },
  { value: 'poor', label: 'Poor', factor: 0.6 }
];

const LIQUIDITY_OPTIONS = [
  { value: 'high', label: 'High (< 30 days)', color: 'text-green-600' },
  { value: 'medium', label: 'Medium (30-90 days)', color: 'text-yellow-600' },
  { value: 'low', label: 'Low (> 90 days)', color: 'text-red-600' }
];

const DEPRECIATION_METHODS = [
  { value: 'none', label: 'No Depreciation' },
  { value: 'straight-line', label: 'Straight Line' },
  { value: 'declining-balance', label: 'Declining Balance' },
  { value: 'market-based', label: 'Market Based' }
];

const FINANCING_TYPES = [
  { value: 'cash', label: 'Cash Purchase' },
  { value: 'mortgage', label: 'Mortgage' },
  { value: 'auto_loan', label: 'Auto Loan' },
  { value: 'personal_loan', label: 'Personal Loan' },
  { value: 'business_loan', label: 'Business Loan' },
  { value: 'heloc', label: 'HELOC' },
  { value: 'other', label: 'Other Financing' }
];

const PROPERTY_TYPES = [
  { value: 'single_family', label: 'Single Family Home' },
  { value: 'condo', label: 'Condominium' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'multi_family', label: 'Multi-Family' },
  { value: 'land', label: 'Land/Lot' },
  { value: 'commercial', label: 'Commercial Property' }
];

// US States for dropdown
const US_STATES = [
  { value: 'AL', label: 'Alabama' }, { value: 'AK', label: 'Alaska' }, { value: 'AZ', label: 'Arizona' },
  { value: 'AR', label: 'Arkansas' }, { value: 'CA', label: 'California' }, { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' }, { value: 'DE', label: 'Delaware' }, { value: 'DC', label: 'District of Columbia' },
  { value: 'FL', label: 'Florida' }, { value: 'GA', label: 'Georgia' }, { value: 'HI', label: 'Hawaii' },
  { value: 'ID', label: 'Idaho' }, { value: 'IL', label: 'Illinois' }, { value: 'IN', label: 'Indiana' },
  { value: 'IA', label: 'Iowa' }, { value: 'KS', label: 'Kansas' }, { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' }, { value: 'ME', label: 'Maine' }, { value: 'MD', label: 'Maryland' },
  { value: 'MA', label: 'Massachusetts' }, { value: 'MI', label: 'Michigan' }, { value: 'MN', label: 'Minnesota' },
  { value: 'MS', label: 'Mississippi' }, { value: 'MO', label: 'Missouri' }, { value: 'MT', label: 'Montana' },
  { value: 'NE', label: 'Nebraska' }, { value: 'NV', label: 'Nevada' }, { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' }, { value: 'NM', label: 'New Mexico' }, { value: 'NY', label: 'New York' },
  { value: 'NC', label: 'North Carolina' }, { value: 'ND', label: 'North Dakota' }, { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' }, { value: 'OR', label: 'Oregon' }, { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' }, { value: 'SC', label: 'South Carolina' }, { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' }, { value: 'TX', label: 'Texas' }, { value: 'UT', label: 'Utah' },
  { value: 'VT', label: 'Vermont' }, { value: 'VA', label: 'Virginia' }, { value: 'WA', label: 'Washington' },
  { value: 'WV', label: 'West Virginia' }, { value: 'WI', label: 'Wisconsin' }, { value: 'WY', label: 'Wyoming' }
];

const AssetsSection = ({ assets = [], onAssetsChange, isPartnerMode = false, partnerName = '' }) => {
  const [editingId, setEditingId] = useState(null);
  const [newAsset, setNewAsset] = useState(null);
  const [formData, setFormData] = useState({});
  const [liabilityAssets, setLiabilityAssets] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [assetAllocation, setAssetAllocation] = useState({});
  const [expandedFormSections, setExpandedFormSections] = useState(new Set(['basic']));
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Filter out wallet metadata records from regular assets to avoid duplication
  const safeAssets = Array.isArray(assets) ? 
    assets.filter(asset => !(asset.isWalletManaged && asset.walletAssetId)) : [];

  // Fetch liabilities that have asset categories (only in user mode)
  useEffect(() => {
    if (!isPartnerMode) {
      // Only fetch liability assets in user mode
      fetchLiabilityAssets();
    } else {
      setLiabilityAssets([]);
    }
  }, [refreshTrigger, isPartnerMode]);

  const fetchLiabilityAssets = async () => {
    try {
      const walletDebts = await debtService.getWalletDebtAccounts();
      
      
      // Log each debt with its asset category info for debugging
      walletDebts.forEach((debt, index) => {
      });
      
      // Filter for debts that have asset categories (secured loans)
      const assetLinkedDebts = walletDebts.filter(debt => {
        const hasAssetCategory = debt.assetCategory || debt.loanDetails?.assetCategory;
        const hasOriginalValue = debt.originalAmount || debt.loanDetails?.originalPrincipal;
        
        
        return hasAssetCategory;
      });
      
      
      // Load metadata for wallet assets to enhance them with professional data
      try {
        const allAssets = await getAssets();
        const walletMetadata = Array.isArray(allAssets) ? 
          allAssets.filter(asset => asset.isWalletManaged && asset.walletAssetId) : [];
        
        
        console.log(`📋 Found ${walletMetadata.length} wallet asset metadata records:`, walletMetadata);
        
        // Enhance wallet assets with their metadata while preserving live wallet data
        const enhancedAssetLinkedDebts = assetLinkedDebts.map(debt => {
          const metadata = walletMetadata.find(meta => meta.walletAssetId === debt._id);
          if (metadata) {
            console.log(`🔗 Enhancing ${debt.name} with metadata from record ${metadata._id}`);
            
            // Calculate real-time equity and financial metrics
            const originalAmount = debt.originalAmount || debt.loanDetails?.originalPrincipal || 0;
            const currentBalance = debt.currentBalance || debt.balance || 0;
            const assetValue = metadata.currentValue || originalAmount || 0;
            const equity = Math.max(0, assetValue - currentBalance);
            const loanToValueRatio = assetValue > 0 ? (currentBalance / assetValue) * 100 : 0;
            
            return {
              ...debt,
              // PRIORITY 1: Live wallet financial data (always from wallet)
              currentBalance: currentBalance,
              remainingDebt: currentBalance,
              originalAmount: originalAmount,
              equity: equity,
              loanToValueRatio: loanToValueRatio,
              monthlyPayment: debt.monthlyPayment || debt.loanDetails?.paymentAmount || 0,
              interestRate: debt.interestRate || debt.loanDetails?.interestRate || 0,
              
              // PRIORITY 2: Professional metadata fields (from asset record, with fallbacks)
              name: metadata.name || debt.name,
              type: metadata.type || debt.assetCategory || debt.loanDetails?.assetCategory || 'personal',
              currentValue: assetValue, // Asset value from metadata
              purchasePrice: metadata.purchasePrice || originalAmount || 0,
              location: metadata.location || '',
              notes: metadata.notes || '',
              isShared: metadata.isShared || false,
              condition: metadata.condition || 'good',
              
              // PRIORITY 3: Advanced professional fields (metadata only)
              costBasis: metadata.costBasis || 0,
              improvements: metadata.improvements || 0,
              depreciationMethod: metadata.depreciationMethod || 'none',
              usefulLife: metadata.usefulLife || 0,
              liquidity: metadata.liquidity || 'low', // Real estate/vehicles typically low liquidity
              insuranceCoverage: metadata.insuranceCoverage || 0,
              insuranceProvider: metadata.insuranceProvider || '',
              lastAppraisal: metadata.lastAppraisal || null,
              appraisalValue: metadata.appraisalValue || 0,
              incomeGenerated: metadata.incomeGenerated || 0,
              taxClassification: metadata.taxClassification || 'personal',
              
              // PRIORITY 4: Real estate specific fields
              propertyAddress: metadata.propertyAddress || '',
              propertyType: metadata.propertyType || 'single_family',
              squareFootage: metadata.squareFootage || 0,
              yearBuilt: metadata.yearBuilt || null,
              bedrooms: metadata.bedrooms || 0,
              bathrooms: metadata.bathrooms || 0,
              propertyTaxes: metadata.propertyTaxes || 0,
              hoaFees: metadata.hoaFees || 0,
              rentalIncome: metadata.rentalIncome || 0,
              
              // PRIORITY 5: Vehicle specific fields
              vehicleMake: metadata.vehicleMake || '',
              vehicleModel: metadata.vehicleModel || '',
              vehicleYear: metadata.vehicleYear || null,
              vinNumber: metadata.vinNumber || '',
              mileage: metadata.mileage || 0,
              licensePlate: metadata.licensePlate || '',
              
              // PRIORITY 6: Financing fields (metadata only)
              financingType: metadata.financingType || '',
              downPayment: metadata.downPayment || 0,
              loanBalance: metadata.loanBalance || 0,
              loanTerm: metadata.loanTerm || 0,
              lenderName: metadata.lenderName || '',
              
              // System fields
              hasMetadata: true,
              metadataId: metadata._id,
              walletAssetId: debt._id,
              isWalletManaged: true,
              lastSyncedAt: new Date(),
              
              // Financial summary for display
              financialSummary: {
                assetValue: assetValue,
                remainingDebt: currentBalance,
                equity: equity,
                equityPercentage: assetValue > 0 ? (equity / assetValue) * 100 : 0,
                loanToValueRatio: loanToValueRatio,
                monthlyPayment: debt.monthlyPayment || debt.loanDetails?.paymentAmount || 0,
                payoffProgress: originalAmount > 0 ? ((originalAmount - currentBalance) / originalAmount) * 100 : 0
              }
            };
          }
          
          // For wallet assets without metadata, provide basic enhancement
          const originalAmount = debt.originalAmount || debt.loanDetails?.originalPrincipal || 0;
          const currentBalance = debt.currentBalance || debt.balance || 0;
          
          return {
            ...debt,
            // Basic wallet data with calculated fields
            remainingDebt: currentBalance,
            equity: Math.max(0, originalAmount - currentBalance),
            loanToValueRatio: originalAmount > 0 ? (currentBalance / originalAmount) * 100 : 0,
            hasMetadata: false,
            isWalletManaged: true,
            type: debt.assetCategory || debt.loanDetails?.assetCategory || 'personal',
            currentValue: originalAmount, // Use original amount as estimated value
            financialSummary: {
              assetValue: originalAmount,
              remainingDebt: currentBalance,
              equity: Math.max(0, originalAmount - currentBalance),
              equityPercentage: originalAmount > 0 ? (Math.max(0, originalAmount - currentBalance) / originalAmount) * 100 : 0,
              loanToValueRatio: originalAmount > 0 ? (currentBalance / originalAmount) * 100 : 0,
              monthlyPayment: debt.monthlyPayment || debt.loanDetails?.paymentAmount || 0,
              payoffProgress: originalAmount > 0 ? ((originalAmount - currentBalance) / originalAmount) * 100 : 0
            }
          };
        });
        
        setLiabilityAssets(enhancedAssetLinkedDebts);
        console.log('✅ Liability assets enhanced with metadata:', enhancedAssetLinkedDebts);
      } catch (metadataError) {
        console.error('❌ Error loading wallet asset metadata:', metadataError);
        // Fall back to basic liability assets if metadata loading fails
        setLiabilityAssets(assetLinkedDebts);
      }
    } catch (error) {
      console.error('❌ Error fetching liability assets:', error);
      setLiabilityAssets([]);
    }
  };

  // Calculate asset allocation for professional view
  useEffect(() => {
    const calculateAllocation = () => {
      const totalValue = calculateTotalValue();
      const allocation = {};
      
      ASSET_TYPES.forEach(type => {
        const typeAssets = safeAssets.filter(asset => asset.type === type.value);
        const typeValue = typeAssets.reduce((sum, asset) => {
          // For real estate assets, use currentValue (home value)
          if (asset.type === 'real_estate') {
            return sum + (asset.currentValue || 0);
          }
          // For other assets, use currentValue as normal
          return sum + (asset.currentValue || 0);
        }, 0);
        const percentage = totalValue > 0 ? (typeValue / totalValue) * 100 : 0;
        
        if (percentage > 0) {
          allocation[type.value] = {
            label: type.label,
            value: typeValue,
            percentage: percentage,
            color: type.color,
            count: typeAssets.length
          };
        }
      });
      
      setAssetAllocation(allocation);
    };
    
    calculateAllocation();
  }, [safeAssets]);

  // Expose refresh function to window for external triggering
  useEffect(() => {
    window.refreshLiabilityAssets = () => {
      console.log('🔄 External trigger: Refreshing liability assets...');
      // Clear debt service cache to ensure fresh data
      debtService.clearCache();
      setRefreshTrigger(prev => prev + 1);
    };
    
    return () => {
      delete window.refreshLiabilityAssets;
    };
  }, []);

  const toggleRowExpansion = (assetId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(assetId)) {
      newExpanded.delete(assetId);
    } else {
      newExpanded.add(assetId);
    }
    setExpandedRows(newExpanded);
  };

  const getAssetTypeInfo = (type) => {
    return ASSET_TYPES.find(t => t.value === type) || ASSET_TYPES[6]; // Default to personal property
  };

  const formatCurrency = (amount) => {
    const safeAmount = safeNumber(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(safeAmount);
  };

  const toggleFormSection = (section) => {
    const newExpanded = new Set(expandedFormSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedFormSections(newExpanded);
  };

  const renderSectionToggle = (sectionKey, title, icon) => (
    <button
      type="button"
      onClick={() => toggleFormSection(sectionKey)}
      className="flex items-center justify-between w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors"
    >
      <div className="flex items-center">
        {icon}
        <span className="ml-2 text-sm font-medium text-gray-700">{title}</span>
      </div>
      {expandedFormSections.has(sectionKey) ? (
        <ChevronUpIcon className="h-4 w-4 text-gray-500" />
      ) : (
        <ChevronDownIcon className="h-4 w-4 text-gray-500" />
      )}
    </button>
  );

  const calculateDepreciation = (asset) => {
    if (!asset.depreciationMethod || asset.depreciationMethod === 'none') return 0;
    
    const purchaseDate = new Date(asset.purchaseDate);
    const currentDate = new Date();
    const yearsOwned = (currentDate - purchaseDate) / (365.25 * 24 * 60 * 60 * 1000);
    
    const usefulLife = safeNumber(asset.usefulLife) || 10; // Default 10 years
    const purchasePrice = safeNumber(asset.purchasePrice);
    
    let depreciation = 0;
    switch (asset.depreciationMethod) {
      case 'straight-line':
        depreciation = Math.min((purchasePrice / usefulLife) * yearsOwned, purchasePrice);
        break;
      case 'declining-balance':
        const rate = 2 / usefulLife; // Double declining balance
        depreciation = purchasePrice * (1 - Math.pow(1 - rate, yearsOwned));
        break;
      default:
        depreciation = 0;
    }
    
    return safeNumber(depreciation);
  };

  const calculateROI = (asset) => {
    const purchasePrice = safeNumber(asset.purchasePrice);
    const currentValue = safeNumber(asset.currentValue);
    const improvements = safeNumber(asset.improvements);
    const totalCost = purchasePrice + improvements;
    
    if (totalCost === 0) return 0;
    
    const roi = ((currentValue - totalCost) / totalCost) * 100;
    return safeNumber(roi);
  };

  const getLiquidityInfo = (liquidityLevel) => {
    return LIQUIDITY_OPTIONS.find(opt => opt.value === liquidityLevel) || LIQUIDITY_OPTIONS[1];
  };

  const getInsuranceCoverage = (asset) => {
    const insuranceValue = safeNumber(asset.insuranceCoverage);
    const currentValue = safeNumber(asset.currentValue);
    const coverageRatio = currentValue > 0 ? (insuranceValue / currentValue) * 100 : 0;
    const safeCoverageRatio = safeNumber(coverageRatio);
    
    return {
      amount: insuranceValue,
      ratio: safeCoverageRatio,
      adequate: safeCoverageRatio >= 80, // Industry standard
      gap: Math.max(0, currentValue * 0.8 - insuranceValue)
    };
  };

  const handleEdit = (asset) => {
    console.log('🔧 handleEdit called with asset:', asset);
    console.log('🔧 EDIT MODAL - Financing fields in asset:', {
      financingType: asset.financingType,
      downPayment: asset.downPayment,
      loanBalance: asset.loanBalance,
      monthlyPayment: asset.monthlyPayment,
      interestRate: asset.interestRate,
      loanTerm: asset.loanTerm,
      lenderName: asset.lenderName
    });
    setEditingAsset(asset);
    setShowEditModal(true);
  };

  const handleStartNew = () => {
    console.log('🏠 Opening asset creation modal');
    setShowCreateModal(true);
  };

  const handleModalSave = async (assetData, assetId = null) => {
    setIsCreating(true);
    try {
      let result;
      
      // Check if this is a wallet-managed liability asset
      const isLiabilityAsset = liabilityAssets.some(la => la._id === assetId);
      
      if (assetId) {
        if (isLiabilityAsset) {
          // For wallet-managed assets, create/update a metadata record in the assets system
          // This preserves wallet data while allowing professional metadata to persist
          console.log('🏦 Updating wallet-managed asset metadata with data:', assetData, 'ID:', assetId);
          
          // Find the original wallet asset
          const originalWalletAsset = liabilityAssets.find(asset => asset._id === assetId);
          
          // Create metadata payload that references the wallet asset
          const metadataPayload = {
            // Basic required fields
            name: assetData.name || originalWalletAsset.name || 'Wallet Asset',
            type: assetData.type || originalWalletAsset.assetCategory || 'personal',
            currentValue: parseFloat(assetData.currentValue) || parseFloat(originalWalletAsset.currentValue) || parseFloat(originalWalletAsset.originalAmount) || 0,
            
            // Optional fields from form data
            purchasePrice: parseFloat(assetData.purchasePrice) || parseFloat(originalWalletAsset.originalAmount) || 0,
            purchaseDate: assetData.purchaseDate || originalWalletAsset.purchaseDate || null,
            condition: assetData.condition || 'good',
            location: assetData.location || '',
            description: assetData.description || '',
            notes: assetData.notes || '',
            isShared: Boolean(assetData.isShared),
            category: assetData.category || '',
            subCategory: assetData.subCategory || '',
            serialNumber: assetData.serialNumber || '',
            model: assetData.model || '',
            brand: assetData.brand || '',
                         year: assetData.year && parseInt(assetData.year) >= 1800 && parseInt(assetData.year) <= (new Date().getFullYear() + 10) ? parseInt(assetData.year) : undefined,
            insuranceValue: parseFloat(assetData.insuranceValue) || 0,
            depreciationRate: parseFloat(assetData.depreciationRate) || 0,
            
            // Professional fields (optional)
            costBasis: parseFloat(assetData.costBasis) || 0,
            improvements: parseFloat(assetData.improvements) || 0,
            depreciationMethod: assetData.depreciationMethod || 'none',
            usefulLife: parseInt(assetData.usefulLife) || 0,
            liquidity: assetData.liquidity || 'medium',
            insuranceCoverage: parseFloat(assetData.insuranceCoverage) || 0,
            insuranceProvider: assetData.insuranceProvider || '',
            lastAppraisal: assetData.lastAppraisal || null,
            appraisalValue: parseFloat(assetData.appraisalValue) || 0,
            incomeGenerated: parseFloat(assetData.incomeGenerated) || 0,
            taxClassification: assetData.taxClassification || 'personal',
            
            // Financing fields (optional)
            financingType: assetData.financingType || 'cash',
            downPayment: parseFloat(assetData.downPayment) || 0,
            loanBalance: parseFloat(assetData.loanBalance) || 0,
            monthlyPayment: parseFloat(assetData.monthlyPayment) || 0,
            interestRate: parseFloat(assetData.interestRate) || 0,
            loanTerm: parseInt(assetData.loanTerm) || 0,
            lenderName: assetData.lenderName || '',
            
            // Real estate fields (optional)
            propertyAddress: assetData.propertyAddress || '',
            propertyType: assetData.propertyType || 'single_family',
            squareFootage: parseFloat(assetData.squareFootage) || 0,
                         yearBuilt: assetData.yearBuilt && parseInt(assetData.yearBuilt) >= 1800 && parseInt(assetData.yearBuilt) <= (new Date().getFullYear() + 10) ? parseInt(assetData.yearBuilt) : undefined,
            bedrooms: parseInt(assetData.bedrooms) || 0,
            bathrooms: parseFloat(assetData.bathrooms) || 0,
            propertyTaxes: parseFloat(assetData.propertyTaxes) || 0,
            hoaFees: parseFloat(assetData.hoaFees) || 0,
            rentalIncome: parseFloat(assetData.rentalIncome) || 0,
            
            // Vehicle fields (optional)
            vehicleMake: assetData.vehicleMake || '',
            vehicleModel: assetData.vehicleModel || '',
                         vehicleYear: assetData.vehicleYear && parseInt(assetData.vehicleYear) >= 1800 && parseInt(assetData.vehicleYear) <= (new Date().getFullYear() + 10) ? parseInt(assetData.vehicleYear) : undefined,
            vinNumber: assetData.vinNumber || '',
            mileage: parseInt(assetData.mileage) || 0,
            licensePlate: assetData.licensePlate || '',
            
            // Reference fields to link back to wallet asset - CRITICAL for detection
            walletAssetId: assetId,
            isWalletManaged: true
          };
          
          // Clean up the payload - remove null values, empty strings, and invalid values for optional fields
          // BUT PRESERVE CRITICAL REFERENCE FIELDS AND MEANINGFUL ZEROS
          Object.keys(metadataPayload).forEach(key => {
            // Never delete these critical fields
            if (key === 'walletAssetId' || key === 'isWalletManaged') {
              return;
            }
            
            // Preserve certain fields even if they're 0 or have default values (meaningful financial data)
            const meaningfulZeroFields = [
              'downPayment', 'loanBalance', 'monthlyPayment', 'interestRate', 'loanTerm',
              'costBasis', 'improvements', 'insuranceCoverage', 'appraisalValue', 'incomeGenerated',
              'mileage', 'bedrooms', 'bathrooms', 'propertyTaxes', 'hoaFees', 'rentalIncome'
            ];
            
            // Preserve meaningful string fields even if they have default values
            const meaningfulStringFields = [
              'financingType', 'lenderName', 'depreciationMethod', 'liquidity', 'taxClassification',
              'propertyType', 'vehicleMake', 'vehicleModel', 'vinNumber', 'licensePlate'
            ];
            
            if (meaningfulZeroFields.includes(key) && typeof metadataPayload[key] === 'number' && metadataPayload[key] === 0) {
              return; // Keep meaningful zeros
            }
            
            if (meaningfulStringFields.includes(key) && typeof metadataPayload[key] === 'string' && metadataPayload[key] !== '') {
              return; // Keep meaningful string values
            }
            
            if (metadataPayload[key] === null || metadataPayload[key] === '' || metadataPayload[key] === undefined || 
                (typeof metadataPayload[key] === 'number' && isNaN(metadataPayload[key]))) {
              delete metadataPayload[key];
            }
          });
          
                               // Ensure required fields are present and valid
          if (!metadataPayload.name) metadataPayload.name = 'Wallet Asset';
          if (!metadataPayload.type) metadataPayload.type = 'personal';
          if (typeof metadataPayload.currentValue !== 'number' || metadataPayload.currentValue < 0) {
            metadataPayload.currentValue = 0;
          }
          
          // ABSOLUTELY ENSURE critical reference fields are present
          metadataPayload.walletAssetId = assetId;
          metadataPayload.isWalletManaged = true;
          
          console.log('🔍 Final metadata payload AFTER cleanup:', { ...metadataPayload });
          console.log('🔍 Critical fields check AFTER cleanup:', {
            assetId: assetId,
            walletAssetId: metadataPayload.walletAssetId,
            isWalletManaged: metadataPayload.isWalletManaged,
            hasWalletAssetId: !!metadataPayload.walletAssetId,
            hasIsWalletManaged: !!metadataPayload.isWalletManaged,
            fieldsMatch: metadataPayload.walletAssetId === assetId
          });
           
           // Validate numeric fields that have constraints
           if (metadataPayload.depreciationRate !== undefined && (metadataPayload.depreciationRate < 0 || metadataPayload.depreciationRate > 100)) {
             delete metadataPayload.depreciationRate;
           }
           
           // Ensure text fields don't exceed maxlength constraints
           const textFieldLimits = {
             name: 100,
             location: 100,
             description: 500,
             notes: 1000,
             category: 50,
             subCategory: 50,
             serialNumber: 100,
             model: 100,
             brand: 50,
             insuranceProvider: 100,
             lenderName: 100,
             propertyAddress: 200,
             vehicleMake: 50,
             vehicleModel: 50,
             vinNumber: 50,
             licensePlate: 20
           };
           
           Object.keys(textFieldLimits).forEach(field => {
             if (metadataPayload[field] && metadataPayload[field].length > textFieldLimits[field]) {
               metadataPayload[field] = metadataPayload[field].substring(0, textFieldLimits[field]);
             }
           });
          
          console.log('🔍 Final metadata payload BEFORE cleanup:', { ...metadataPayload });
          
          // Verify critical fields are present BEFORE cleanup
          console.log('🔍 Critical fields check BEFORE cleanup:', {
            assetId: assetId,
            assetIdType: typeof assetId,
            assetIdLength: assetId?.length,
            walletAssetId: metadataPayload.walletAssetId,
            isWalletManaged: metadataPayload.isWalletManaged,
            hasWalletAssetId: !!metadataPayload.walletAssetId,
            hasIsWalletManaged: !!metadataPayload.isWalletManaged
          });
          
          try {
            // CRITICAL FIX: Fetch fresh assets data to ensure we have the latest state
            console.log('🔄 Fetching fresh assets data before metadata detection...');
            const freshAssets = await getAssets();
            console.log('📋 Fresh assets fetched:', freshAssets?.length || 0, 'assets');
            
            // Use fresh assets instead of potentially stale props
            const assetsToSearch = Array.isArray(freshAssets) ? freshAssets : assets;
            
            // AGGRESSIVE FIX: First check if any existing asset has the same name and loan ID
            const nameBasedMatch = assetsToSearch.find(asset => 
              asset.name === (assetData.name || originalWalletAsset.name) && 
              asset.walletAssetId === assetId
            );
            
            // Also check the enhanced liability assets for existing metadata
            const enhancedAssetMatch = liabilityAssets.find(la => 
              la._id === assetId && la.hasMetadata && la.metadataId
            );
            
            let existingMetadata = nameBasedMatch || 
              (enhancedAssetMatch ? assetsToSearch.find(a => a._id === enhancedAssetMatch.metadataId) : null) ||
              assetsToSearch.find(asset => asset.walletAssetId === assetId);
            
            console.log('🔍 COMPREHENSIVE METADATA DEBUG:', { 
              searchingForAssetId: assetId,
              assetName: assetData.name || originalWalletAsset.name,
              staleAssetsCount: assets?.length || 0,
              freshAssetsCount: assetsToSearch?.length || 0,
              nameBasedMatch: nameBasedMatch ? nameBasedMatch._id : 'none',
              enhancedAssetMatch: enhancedAssetMatch ? enhancedAssetMatch.metadataId : 'none',
              finalExistingMetadata: existingMetadata ? existingMetadata._id : 'NONE - WILL CREATE NEW',
              freshAssetsDetailed: assetsToSearch?.map(a => ({ 
                id: a._id, 
                name: a.name,
                walletAssetId: a.walletAssetId, 
                isWalletManaged: a.isWalletManaged,
                nameMatches: a.name === (assetData.name || originalWalletAsset.name),
                idMatches: a.walletAssetId === assetId
              }))
            });
            
            // DEBUG: Log the final payload being sent to API
            console.log('💾 PAYLOAD BEING SENT TO API:', {
              financing: {
                financingType: metadataPayload.financingType,
                downPayment: metadataPayload.downPayment,
                loanBalance: metadataPayload.loanBalance,
                monthlyPayment: metadataPayload.monthlyPayment,
                interestRate: metadataPayload.interestRate,
                loanTerm: metadataPayload.loanTerm,
                lenderName: metadataPayload.lenderName
              },
              professional: {
                costBasis: metadataPayload.costBasis,
                improvements: metadataPayload.improvements,
                depreciationMethod: metadataPayload.depreciationMethod,
                liquidity: metadataPayload.liquidity,
                insuranceCoverage: metadataPayload.insuranceCoverage,
                taxClassification: metadataPayload.taxClassification
              },
              vehicle: {
                vehicleMake: metadataPayload.vehicleMake,
                vehicleModel: metadataPayload.vehicleModel,
                vehicleYear: metadataPayload.vehicleYear,
                mileage: metadataPayload.mileage
              }
            });

            if (existingMetadata) {
              // Update existing metadata record
              console.log('🔄 Updating existing wallet asset metadata record:', existingMetadata._id);
              result = await updateAsset(existingMetadata._id, metadataPayload);
              console.log('✅ Successfully updated existing metadata record');
            } else {
              // For wallet-managed assets without existing metadata, create a metadata record
              console.log('📝 Creating new metadata record for wallet asset:', assetId);
              result = await createAsset(metadataPayload);
              console.log('✅ Metadata record created for wallet asset');
            }
            
            // Update the liability asset in local state with ALL the new metadata
            console.log('🔍 LOCAL STATE UPDATE - assetData received:', {
              financingFields: {
                financingType: assetData.financingType,
                downPayment: assetData.downPayment,
                loanBalance: assetData.loanBalance,
                monthlyPayment: assetData.monthlyPayment,
                interestRate: assetData.interestRate,
                loanTerm: assetData.loanTerm,
                lenderName: assetData.lenderName
              }
            });
            
            const updatedLiabilityAssets = liabilityAssets.map(asset => {
              if (asset._id === assetId) {
                return {
                  ...asset,
                  // Apply ALL the editable fields to local display
                  name: assetData.name || asset.name,
                  type: assetData.type || asset.type,
                  location: assetData.location || asset.location,
                  notes: assetData.notes || asset.notes,
                  isShared: assetData.isShared || asset.isShared,
                  condition: assetData.condition || asset.condition,
                  purchaseDate: assetData.purchaseDate !== undefined ? assetData.purchaseDate : asset.purchaseDate,
                  costBasis: assetData.costBasis !== undefined ? assetData.costBasis : asset.costBasis,
                  improvements: assetData.improvements !== undefined ? assetData.improvements : asset.improvements,
                  depreciationMethod: assetData.depreciationMethod !== undefined ? assetData.depreciationMethod : asset.depreciationMethod,
                  usefulLife: assetData.usefulLife || asset.usefulLife,
                  liquidity: assetData.liquidity || asset.liquidity,
                  insuranceCoverage: assetData.insuranceCoverage || asset.insuranceCoverage,
                  insuranceProvider: assetData.insuranceProvider || asset.insuranceProvider,
                  lastAppraisal: assetData.lastAppraisal || asset.lastAppraisal,
                  appraisalValue: assetData.appraisalValue || asset.appraisalValue,
                  incomeGenerated: assetData.incomeGenerated || asset.incomeGenerated,
                  taxClassification: assetData.taxClassification || asset.taxClassification,
                  // Vehicle details
                  vehicleMake: assetData.vehicleMake || asset.vehicleMake,
                  vehicleModel: assetData.vehicleModel || asset.vehicleModel,
                  vehicleYear: assetData.vehicleYear || asset.vehicleYear,
                  vinNumber: assetData.vinNumber || asset.vinNumber,
                  mileage: assetData.mileage || asset.mileage,
                  licensePlate: assetData.licensePlate || asset.licensePlate,
                  // Financing details - use proper null checking to preserve zeros
                  financingType: assetData.financingType !== undefined ? assetData.financingType : asset.financingType,
                  downPayment: assetData.downPayment !== undefined ? assetData.downPayment : asset.downPayment,
                  loanBalance: assetData.loanBalance !== undefined ? assetData.loanBalance : asset.loanBalance,
                  monthlyPayment: assetData.monthlyPayment !== undefined ? assetData.monthlyPayment : asset.monthlyPayment,
                  interestRate: assetData.interestRate !== undefined ? assetData.interestRate : asset.interestRate,
                  loanTerm: assetData.loanTerm !== undefined ? assetData.loanTerm : asset.loanTerm,
                  lenderName: assetData.lenderName !== undefined ? assetData.lenderName : asset.lenderName,
                  // Real estate details
                  propertyAddress: assetData.propertyAddress || asset.propertyAddress,
                  propertyType: assetData.propertyType || asset.propertyType,
                  squareFootage: assetData.squareFootage || asset.squareFootage,
                  yearBuilt: assetData.yearBuilt || asset.yearBuilt,
                  bedrooms: assetData.bedrooms || asset.bedrooms,
                  bathrooms: assetData.bathrooms || asset.bathrooms,
                  propertyTaxes: assetData.propertyTaxes || asset.propertyTaxes,
                  hoaFees: assetData.hoaFees || asset.hoaFees,
                  rentalIncome: assetData.rentalIncome || asset.rentalIncome,
                  // Flag to indicate it has persistent metadata
                  hasMetadata: true,
                  metadataId: result._id
                };
              }
              return asset;
            });
            
            setLiabilityAssets(updatedLiabilityAssets);
            console.log('✅ Updated local liability asset with ALL metadata fields');
            console.log('🔍 Updated asset preview:', updatedLiabilityAssets.find(a => a._id === assetId));
            console.log('✅ Wallet-managed asset metadata saved and local state updated:', result);
            
            // Also refresh the assets list to include the metadata record
            try {
              console.log('🔄 Refreshing assets to include new metadata record...');
              // Small delay to ensure database consistency
              await new Promise(resolve => setTimeout(resolve, 100));
              const refreshedAssets = await getAssets();
              console.log('📋 Refreshed assets after metadata creation:', refreshedAssets?.length || 0);
              if (Array.isArray(refreshedAssets)) {
                onAssetsChange(refreshedAssets);
                console.log('✅ Assets refreshed successfully after metadata creation');
              } else if (refreshedAssets?.data && Array.isArray(refreshedAssets.data)) {
                onAssetsChange(refreshedAssets.data);
                console.log('✅ Assets refreshed successfully from .data after metadata creation');
              }
            } catch (refreshError) {
              console.error('❌ Error refreshing assets after wallet metadata update:', refreshError);
            }
            
            // CRITICAL: Force refresh of liability assets to show updated metadata
            try {
              console.log('🔄 Force refreshing liability assets to display updated metadata...');
              if (typeof fetchLiabilityAssets === 'function') {
                await fetchLiabilityAssets();
                console.log('✅ Liability assets force refreshed - metadata should now be visible');
              } else {
                console.log('⚠️ fetchLiabilityAssets function not available for refresh');
              }
            } catch (liabilityRefreshError) {
              console.error('❌ Error refreshing liability assets:', liabilityRefreshError);
            }
          } catch (metadataError) {
            console.error('❌ Error saving wallet asset metadata:', metadataError);
            console.error('❌ Error response:', metadataError.response?.data);
            console.error('❌ Error status:', metadataError.response?.status);
            
            // Fall back to local-only update if API fails
            const updatedLiabilityAssets = liabilityAssets.map(asset => {
              if (asset._id === assetId) {
                return { ...asset, ...assetData };
              }
              return asset;
            });
            setLiabilityAssets(updatedLiabilityAssets);
            result = updatedLiabilityAssets.find(asset => asset._id === assetId);
            console.log('⚠️ Fallback: Wallet asset updated locally only');
            
            // Show error to user
            alert(`Error saving asset metadata: ${metadataError.response?.data?.message || metadataError.message}. Changes saved locally only.`);
          }
        } else {
          // Regular asset update via API
          console.log('🏠 Updating regular asset via API with data:', assetData, 'ID:', assetId);
          result = await updateAsset(assetId, assetData);
          console.log('✅ Asset updated successfully via API:', result);
        }
      } else {
        // Creating new asset
        console.log('🏠 Creating new asset via modal with data:', assetData);
        
        // Clean up the assetData for creation
        const cleanAssetData = {
          ...assetData,
          // Ensure required fields
          name: assetData.name || 'New Asset',
          type: assetData.type || 'personal',
          currentValue: parseFloat(assetData.currentValue) || 0
        };
        
        // Remove null/undefined values
        Object.keys(cleanAssetData).forEach(key => {
          if (cleanAssetData[key] === null || cleanAssetData[key] === undefined) {
            delete cleanAssetData[key];
          }
        });
        
        result = await createAsset(cleanAssetData);
        console.log('✅ Asset created successfully via modal:', result);
      }
      
      // Refresh the assets list from server (only for regular assets, not wallet-managed)
      if (!isLiabilityAsset) {
        try {
          const refreshedAssets = await getAssets();
          if (Array.isArray(refreshedAssets)) {
            onAssetsChange(refreshedAssets);
          } else if (refreshedAssets?.data && Array.isArray(refreshedAssets.data)) {
            onAssetsChange(refreshedAssets.data);
          } else {
            // Fallback to local state update
            if (assetId) {
              onAssetsChange(safeAssets.map(a => a._id === assetId ? result : a));
            } else {
              onAssetsChange([...safeAssets, result]);
            }
          }
        } catch (refreshError) {
          console.error('❌ Error refreshing assets:', refreshError);
          // Fallback to local state update
          if (assetId) {
            onAssetsChange(safeAssets.map(a => a._id === assetId ? result : a));
          } else {
            onAssetsChange([...safeAssets, result]);
          }
        }
      }
      
      // Small delay to ensure all async operations complete before closing modal
      await new Promise(resolve => setTimeout(resolve, 150));
      
      // Close the appropriate modal
      if (assetId) {
        setShowEditModal(false);
        setEditingAsset(null);
      } else {
        setShowCreateModal(false);
      }
    } catch (error) {
      console.error('❌ Error saving asset via modal:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      alert(`Error ${assetId ? 'updating' : 'creating'} asset: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setNewAsset(null);
    setFormData({});
  };

  const handleSave = async () => {
    try {
      const assetData = {
        ...formData,
        currentValue: parseFloat(formData.currentValue) || 0,
        purchasePrice: parseFloat(formData.purchasePrice) || 0,
        // Convert numeric fields
        costBasis: parseFloat(formData.costBasis) || 0,
        improvements: parseFloat(formData.improvements) || 0,
        usefulLife: parseInt(formData.usefulLife) || 0,
        insuranceCoverage: parseFloat(formData.insuranceCoverage) || 0,
        appraisalValue: parseFloat(formData.appraisalValue) || 0,
        incomeGenerated: parseFloat(formData.incomeGenerated) || 0,
        downPayment: parseFloat(formData.downPayment) || 0,
        loanBalance: parseFloat(formData.loanBalance) || 0,
        monthlyPayment: parseFloat(formData.monthlyPayment) || 0,
        interestRate: parseFloat(formData.interestRate) || 0,
        loanTerm: parseInt(formData.loanTerm) || 0,
        squareFootage: parseFloat(formData.squareFootage) || 0,
        yearBuilt: parseInt(formData.yearBuilt) || 0,
        bedrooms: parseInt(formData.bedrooms) || 0,
        bathrooms: parseFloat(formData.bathrooms) || 0,
        propertyTaxes: parseFloat(formData.propertyTaxes) || 0,
        hoaFees: parseFloat(formData.hoaFees) || 0,
        rentalIncome: parseFloat(formData.rentalIncome) || 0,
        vehicleYear: parseInt(formData.vehicleYear) || 0,
        mileage: parseInt(formData.mileage) || 0
      };

      console.log('🏠 Asset Save Attempt:', {
        isNewAsset: !!newAsset,
        assetData: assetData,
        formData: formData
      });

      if (newAsset) {
        // Creating new asset
        console.log('🏠 Creating new asset with data:', assetData);
        const createdAsset = await createAsset(assetData);
        console.log('✅ Asset created successfully:', createdAsset);
        
        // Refresh the assets list from server to ensure we have the latest data
        try {
          console.log('🔄 Calling getAssets() to refresh from server...');
          const refreshedAssets = await getAssets();
          console.log('🔄 Raw getAssets response:', refreshedAssets);
          console.log('🔄 Response type:', typeof refreshedAssets);
          console.log('🔄 Is array?', Array.isArray(refreshedAssets));
          
          if (Array.isArray(refreshedAssets)) {
            console.log('✅ Setting refreshed assets:', refreshedAssets.length, 'assets');
            onAssetsChange(refreshedAssets);
          } else if (refreshedAssets?.data && Array.isArray(refreshedAssets.data)) {
            console.log('✅ Setting refreshed assets from .data:', refreshedAssets.data.length, 'assets');
            onAssetsChange(refreshedAssets.data);
          } else {
            console.warn('⚠️ Unexpected response format, using fallback');
            onAssetsChange([...safeAssets, createdAsset]);
          }
        } catch (refreshError) {
          console.error('❌ Error refreshing assets:', refreshError);
          console.error('❌ Error details:', {
            message: refreshError.message,
            response: refreshError.response?.data,
            status: refreshError.response?.status
          });
          // Fallback to local state update
          onAssetsChange([...safeAssets, createdAsset]);
        }
        
        setNewAsset(null);
      } else {
        // Updating existing asset
        console.log('🏠 Updating asset:', editingId, assetData);
        const updatedAsset = await updateAsset(editingId, assetData);
        console.log('✅ Asset updated successfully:', updatedAsset);
        
        // Refresh the assets list from server to ensure we have the latest data
        try {
          const refreshedAssets = await getAssets();
          console.log('🔄 Refreshed assets from server:', refreshedAssets);
          onAssetsChange(Array.isArray(refreshedAssets) ? refreshedAssets : []);
        } catch (refreshError) {
          console.error('❌ Error refreshing assets:', refreshError);
          // Fallback to local state update
          onAssetsChange(safeAssets.map(a => a._id === editingId ? updatedAsset : a));
        }
      }

      setEditingId(null);
      setFormData({});
      console.log('✅ Asset save completed successfully');
    } catch (error) {
      console.error('❌ Error saving asset:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      // You might want to show an error message to the user here
      alert(`Error saving asset: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this asset?')) {
      try {
        await deleteAsset(id);
        onAssetsChange(safeAssets.filter(a => a._id !== id));
      } catch (error) {
        console.error('Error deleting asset:', error);
      }
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const renderEditableCell = (field, type = 'text', options = null) => {
    const value = formData[field] || '';
    
    if (type === 'select' && options) {
      return (
        <select
          value={value}
          onChange={(e) => handleInputChange(field, e.target.value)}
          className="w-full p-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          {options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    if (type === 'checkbox') {
      return (
        <input
          type="checkbox"
          checked={value}
          onChange={(e) => handleInputChange(field, e.target.checked)}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
      );
    }

    return (
      <input
        type={type}
        value={value}
        onChange={(e) => handleInputChange(field, e.target.value)}
        className="w-full p-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        placeholder={field === 'currentValue' || field === 'purchasePrice' ? '0' : ''}
      />
    );
  };

  const calculateTotalValue = () => {
    const assetsValue = safeAssets.reduce((total, asset) => {
      // For real estate assets, use currentValue (home value) not mortgage balance
      if (asset.type === 'real_estate') {
        return total + safeNumber(asset.currentValue);
      }
      // For other assets, use currentValue as normal
      return total + safeNumber(asset.currentValue);
    }, 0);
    
    // For liability assets, we estimate the asset value from the original loan amount
    const liabilityAssetsValue = liabilityAssets.reduce((total, debt) => {
      const originalValue = safeNumber(debt.originalAmount || debt.loanDetails?.originalPrincipal);
      return total + originalValue;
    }, 0);
    return assetsValue + liabilityAssetsValue;
  };

  // Mobile-responsive asset card component
  const renderMobileAssetCard = (asset, isLiability = false) => {
    const isExpanded = expandedRows.has(asset._id);
    const isEditing = editingId === asset._id;
    
    // Calculate professional metrics
    const roi = !isLiability ? calculateROI(asset) : null;
    const depreciation = !isLiability ? calculateDepreciation(asset) : null;
    const insurance = !isLiability ? getInsuranceCoverage(asset) : null;
    const liquidityInfo = !isLiability ? getLiquidityInfo(asset.liquidity) : null;
    
    return (
      <div key={asset._id} className="bg-white rounded-xl border border-gray-200 shadow-sm mb-4 overflow-hidden">
        {/* Mobile Card Header */}
        <div className="p-4 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              {!isLiability && (
                <>
                  {(() => {
                    const typeInfo = getAssetTypeInfo(asset.type);
                    const IconComponent = typeInfo.icon;
                    return <IconComponent className={`h-6 w-6 ${typeInfo.color} flex-shrink-0`} />;
                  })()}
                </>
              )}
              {isLiability && (
                <>
                  {(() => {
                    const assetCategory = asset.assetCategory || asset.loanDetails?.assetCategory;
                    const assetInfo = mapAssetCategoryToIcon(assetCategory);
                    const IconComponent = assetInfo.icon;
                    return <IconComponent className={`h-6 w-6 ${assetInfo.color} flex-shrink-0`} />;
                  })()}
                </>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-gray-900 truncate">{asset.name}</h3>
                  {isLiability && (
                    <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded-full font-medium flex-shrink-0">
                      Loan Asset
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 truncate">
                  {!isLiability ? getAssetTypeInfo(asset.type).label : `${mapAssetCategoryToIcon(asset.assetCategory || asset.loanDetails?.assetCategory).label} • ${asset.creditor}`}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2 flex-shrink-0">
              <button
                onClick={() => toggleRowExpansion(asset._id)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                title={isExpanded ? "Hide Details" : "View Details"}
              >
                {isExpanded ? (
                  <ChevronUpIcon className="h-5 w-5" />
                ) : (
                  <ChevronDownIcon className="h-5 w-5" />
                )}
              </button>
              {!isEditing && (
                <button
                  onClick={() => handleEdit(asset)}
                  className={`p-2 hover:bg-blue-100 rounded-lg transition-colors ${
                    isLiability ? 'text-green-600 hover:text-green-800' : 'text-blue-600 hover:text-blue-800'
                  }`}
                  title={isLiability ? "Edit Asset Details" : "Edit Asset"}
                >
                  <PencilIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          
          {/* Key Metrics Row */}
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <div className="text-xs text-gray-500 mb-1">Current Value</div>
              <div className="text-lg font-bold text-emerald-600">
                {formatCurrency(asset.currentValue || (isLiability ? (asset.originalAmount || asset.loanDetails?.originalPrincipal || 0) : 0))}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500 mb-1">
                {isLiability ? "Remaining Debt" : "Equity"}
              </div>
              <div className={`text-lg font-bold ${isLiability ? 'text-red-600' : 'text-green-600'}`}>
                {isLiability 
                  ? formatCurrency(asset.currentBalance || 0)
                  : formatCurrency(asset.currentValue || 0)
                }
              </div>
            </div>
          </div>
        </div>

        {/* Expandable Details - Organized Sections */}
        <Collapse in={isExpanded}>
          <div className="p-4 bg-gray-50 border-t border-gray-100 space-y-4">
            {/* Asset Details Section */}
            <div className="bg-white p-3 rounded-lg border border-gray-200">
              <div className="flex items-center mb-2">
                <div className="w-4 h-4 bg-blue-100 rounded flex items-center justify-center mr-2">
                  <svg className="w-2 h-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h6 className="text-sm font-semibold text-gray-900">Asset Details</h6>
              </div>
              <div className="space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-gray-600">Type:</span>
                  <span className="font-medium capitalize text-gray-900">{asset.type?.replace('_', ' ') || 'Unknown'}</span>
                </div>
                {asset.condition && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Condition:</span>
                    <span className="font-medium capitalize text-gray-900">{asset.condition}</span>
                  </div>
                )}
                {isLiability && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Source:</span>
                    <span className="font-medium text-yellow-600">Wallet Loan</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Current Value:</span>
                  <span className="font-medium text-gray-900">{formatCurrency(asset.currentValue || asset.originalAmount || asset.loanDetails?.originalPrincipal)}</span>
                </div>
                {(asset.purchasePrice || asset.originalAmount) && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Original Amount:</span>
                    <span className="font-medium text-gray-900">{formatCurrency(asset.purchasePrice || asset.originalAmount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Equity:</span>
                  <span className={`font-medium ${(asset.currentValue || asset.originalAmount || 0) - (asset.balance || asset.currentBalance || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency((asset.currentValue || asset.originalAmount || 0) - (asset.balance || asset.currentBalance || 0))}
                  </span>
                </div>
                {isLiability && (asset.balance || asset.currentBalance) && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Remaining Debt:</span>
                    <span className="font-medium text-red-600">{formatCurrency(asset.balance || asset.currentBalance)}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* Vehicle Details Section */}
            {asset.type === 'vehicle' && (asset.vehicleMake || asset.vehicleModel || asset.vehicleYear || asset.mileage || asset.vinNumber) && (
              <div className="bg-white p-3 rounded-lg border border-gray-200">
                <div className="flex items-center mb-2">
                  <div className="w-4 h-4 bg-green-100 rounded flex items-center justify-center mr-2">
                    <svg className="w-2 h-2 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <h6 className="text-sm font-semibold text-gray-900">Vehicle Details</h6>
                </div>
                <div className="space-y-2 text-xs">
                  {asset.vehicleMake && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Make:</span>
                      <span className="font-medium text-gray-900">{asset.vehicleMake}</span>
                    </div>
                  )}
                  {asset.vehicleModel && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Model:</span>
                      <span className="font-medium text-gray-900">{asset.vehicleModel}</span>
                    </div>
                  )}
                  {asset.vehicleYear && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Year:</span>
                      <span className="font-medium text-gray-900">{asset.vehicleYear}</span>
                    </div>
                  )}
                  {asset.mileage && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Mileage:</span>
                      <span className="font-medium text-gray-900">{parseInt(asset.mileage).toLocaleString()} miles</span>
                    </div>
                  )}
                  {asset.vinNumber && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">VIN:</span>
                      <span className="font-medium text-gray-900 font-mono text-xs">{asset.vinNumber}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Financial Information Section */}
            {(asset.financingType || asset.loanBalance || asset.monthlyPayment || asset.interestRate || asset.downPayment || asset.loanTerm || asset.lenderName) && (
              <div className="bg-white p-3 rounded-lg border border-gray-200">
                <div className="flex items-center mb-2">
                  <div className="w-4 h-4 bg-blue-100 rounded flex items-center justify-center mr-2">
                    <svg className="w-2 h-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                  </div>
                  <h6 className="text-sm font-semibold text-gray-900">Financial Information</h6>
                </div>
                <div className="space-y-2 text-xs">
                  {asset.financingType && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Financing Type:</span>
                      <span className="font-medium capitalize text-gray-900">{asset.financingType.replace('_', ' ')}</span>
                    </div>
                  )}
                  {(asset.downPayment !== null && asset.downPayment !== undefined) && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Down Payment:</span>
                      <span className="font-medium text-gray-900">{formatCurrency(asset.downPayment)}</span>
                    </div>
                  )}
                  {(asset.loanBalance !== null && asset.loanBalance !== undefined) && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Loan Balance:</span>
                      <span className="font-medium text-gray-900">{formatCurrency(asset.loanBalance)}</span>
                    </div>
                  )}
                  {asset.monthlyPayment && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Monthly Payment:</span>
                      <span className="font-medium text-gray-900">{formatCurrency(asset.monthlyPayment)}</span>
                    </div>
                  )}
                  {asset.interestRate && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Interest Rate:</span>
                      <span className="font-medium text-gray-900">{parseFloat(asset.interestRate).toFixed(2)}%</span>
                    </div>
                  )}
                  {asset.loanTerm && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Loan Term:</span>
                      <span className="font-medium text-gray-900">{asset.loanTerm} months</span>
                    </div>
                  )}
                  {asset.lenderName && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Lender:</span>
                      <span className="font-medium text-gray-900">{asset.lenderName}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Professional & Tax Information Section */}
            {(asset.costBasis || asset.improvements || asset.depreciationMethod || asset.liquidity || asset.insuranceCoverage || asset.taxClassification || asset.incomeGenerated || asset.appraisalValue) && (
              <div className="bg-white p-3 rounded-lg border border-gray-200">
                <div className="flex items-center mb-2">
                  <div className="w-4 h-4 bg-purple-100 rounded flex items-center justify-center mr-2">
                    <svg className="w-2 h-2 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  <h6 className="text-sm font-semibold text-gray-900">Professional & Tax Information</h6>
                </div>
                <div className="space-y-2 text-xs">
                  {(asset.costBasis !== null && asset.costBasis !== undefined) && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cost Basis:</span>
                      <span className="font-medium text-gray-900">{formatCurrency(asset.costBasis)}</span>
                    </div>
                  )}
                  {(asset.improvements !== null && asset.improvements !== undefined) && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Improvements:</span>
                      <span className="font-medium text-gray-900">{formatCurrency(asset.improvements)}</span>
                    </div>
                  )}
                  {asset.depreciationMethod && asset.depreciationMethod !== 'none' && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Depreciation Method:</span>
                      <span className="font-medium capitalize text-gray-900">{asset.depreciationMethod.replace('-', ' ')}</span>
                    </div>
                  )}
                  {asset.liquidity && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Liquidity:</span>
                      <span className={`font-medium capitalize ${
                        asset.liquidity === 'high' ? 'text-green-600' : 
                        asset.liquidity === 'medium' ? 'text-yellow-600' : 'text-red-600'
                      }`}>
                        {asset.liquidity}
                      </span>
                    </div>
                  )}
                  {(asset.insuranceCoverage !== null && asset.insuranceCoverage !== undefined) && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Insurance Coverage:</span>
                      <span className="font-medium text-gray-900">{formatCurrency(asset.insuranceCoverage)}</span>
                    </div>
                  )}
                  {asset.taxClassification && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tax Classification:</span>
                      <span className="font-medium capitalize text-gray-900">{asset.taxClassification}</span>
                    </div>
                  )}
                  {(asset.incomeGenerated !== null && asset.incomeGenerated !== undefined) && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Monthly Income:</span>
                      <span className="font-medium text-gray-900">{formatCurrency(asset.incomeGenerated)}</span>
                    </div>
                  )}
                  {(asset.appraisalValue !== null && asset.appraisalValue !== undefined) && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Appraisal Value:</span>
                      <span className="font-medium text-gray-900">{formatCurrency(asset.appraisalValue)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

          </div>
        </Collapse>
      </div>
    );
  };

  // Mobile asset card component end
  
  // Desktop table header component  
  const renderTableHeader = () => {
    return (
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Asset</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Equity</th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
        </tr>
      </thead>
    );
  };

  const mapAssetCategoryToIcon = (assetCategory) => {
    const category = assetCategory?.toLowerCase() || '';
    if (category.includes('home') || category.includes('house') || category.includes('residence')) {
      return { icon: HomeIcon, color: 'text-blue-600', label: 'Real Estate' };
    }
    if (category.includes('vehicle') || category.includes('auto') || category.includes('car')) {
      return { icon: TruckIcon, color: 'text-green-600', label: 'Vehicle' };
    }
    if (category.includes('investment')) {
      return { icon: ChartBarIcon, color: 'text-purple-600', label: 'Investment' };
    }
    if (category.includes('education')) {
      return { icon: ComputerDesktopIcon, color: 'text-indigo-600', label: 'Education Asset' };
    }
    return { icon: CubeIcon, color: 'text-gray-600', label: 'Other Asset' };
  };

  const renderLiabilityAssetRow = (debt) => {
    const assetCategory = debt.assetCategory || debt.loanDetails?.assetCategory;
    const assetInfo = mapAssetCategoryToIcon(assetCategory);
    const IconComponent = assetInfo.icon;
    
    // Use enhanced financial data if available, fallback to basic calculation
    const financialData = debt.financialSummary || {
      assetValue: debt.originalAmount || debt.loanDetails?.originalPrincipal || 0,
      remainingDebt: debt.currentBalance || debt.balance || 0,
      equity: Math.max(0, (debt.originalAmount || 0) - (debt.currentBalance || 0))
    };
    
    const isEnhanced = debt.hasMetadata;
    const lastSynced = debt.lastSyncedAt ? new Date(debt.lastSyncedAt).toLocaleTimeString() : null;
    
    return (
      <tr key={`liability-${debt._id}`} className="border-b border-gray-200 hover:bg-amber-50/30 bg-amber-50/10">
        {/* Asset Type Icon & Name */}
        <td className="px-4 py-3 w-80">
          <div className="flex items-center space-x-3">
            <IconComponent className={`h-5 w-5 ${assetInfo.color}`} />
            <div>
              <div className="font-medium text-gray-900 flex items-center gap-2">
                {debt.name}
                <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs rounded-full font-medium">
                  Loan Asset
                </span>
                {isEnhanced && (
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium" title={`Last synced: ${lastSynced}`}>
                    ↻ Synced
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-500">
                {assetInfo.label} • {debt.creditor || debt.institutionName || 'Wallet Loan'}
                {financialData.loanToValueRatio && (
                  <span className="ml-2 text-xs text-blue-600">
                    LTV: {financialData.loanToValueRatio.toFixed(1)}%
                  </span>
                )}
              </div>
            </div>
          </div>
        </td>

        {/* Current/Estimated Value */}
        <td className="px-4 py-3 w-32">
          <span className="font-semibold text-amber-600">
            {formatCurrency(financialData.assetValue)}
          </span>
          <div className="text-xs text-gray-500">
            {isEnhanced ? 'Current Value' : 'Estimated'}
          </div>
        </td>

        {/* Purchase Price / Original Amount */}
        <td className="px-4 py-3 w-32">
          <span className="text-gray-600">
            {formatCurrency(debt.purchasePrice || debt.originalAmount || debt.loanDetails?.originalPrincipal || 0)}
          </span>
          <div className="text-xs text-gray-500">
            {isEnhanced ? 'Purchase Price' : 'Loan Amount'}
          </div>
        </td>

        {/* Equity (Live from Wallet) */}
        <td className="px-4 py-3 w-32">
          <span className={`font-semibold ${financialData.equity >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(financialData.equity)}
          </span>
          <div className="text-xs text-gray-600 flex items-center gap-1">
            {financialData.equity >= 0 ? 'Positive' : 'Negative'}
            {financialData.equityPercentage && (
              <span className="text-xs">
                ({financialData.equityPercentage.toFixed(0)}%)
              </span>
            )}
          </div>
        </td>

        {/* Remaining Debt (Live from Wallet) */}
        <td className="px-4 py-3 w-32">
          <span className="font-semibold text-red-600">
            {formatCurrency(financialData.remainingDebt)}
          </span>
          <div className="text-xs text-gray-500 flex items-center gap-1">
            Remaining
            {financialData.payoffProgress && (
              <span className="text-green-600">
                ({financialData.payoffProgress.toFixed(0)}% paid)
              </span>
            )}
          </div>
        </td>

        {/* Origination Date */}
        <td className="px-4 py-3 w-32">
          <span className="text-gray-600">
            {(debt.startDate || debt.loanDetails?.originatedDate) ? 
              new Date(debt.startDate || debt.loanDetails?.originatedDate).toLocaleDateString() : 
              '-'
            }
          </span>
        </td>

        {/* Loan Details */}
        <td className="px-4 py-3 w-24">
          <div className="text-xs space-y-1">
            {debt.interestRate && (
              <div className="text-blue-600">{debt.interestRate.toFixed(2)}% APR</div>
            )}
            {(debt.termLength || debt.loanDetails?.term) && (
              <div className="text-gray-500">{debt.termLength || debt.loanDetails?.term}</div>
            )}
          </div>
        </td>

        {/* Location - Show from wallet */}
        <td className="px-4 py-3 w-40">
          <span className="text-gray-600 text-sm">Managed in Wallet</span>
        </td>

        {/* Shared */}
        <td className="px-4 py-3 w-20 text-center">
          <span className="inline-flex h-2 w-2 rounded-full bg-gray-300"></span>
        </td>

        {/* Analysis Report - Not available for liability assets */}
        <td className="px-4 py-3 w-32 text-center">
          <span className="text-xs text-gray-400">-</span>
        </td>

        {/* Actions */}
        <td className="px-4 py-3 w-24">
          <div className="flex space-x-1">
            <button
              onClick={() => handleEdit(debt)}
              className="p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded text-xs"
              title="Edit Asset Metadata"
            >
              <PencilIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => window.open('/wallet', '_blank')}
              className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded text-xs"
              title="View in Wallet"
            >
              <EyeIcon className="h-4 w-4" />
            </button>
          </div>
        </td>
      </tr>
    );
  };

  const renderAssetRow = (asset, isNew = false) => {
    const isEditing = editingId === asset._id || (isNew && editingId === newAsset);
    const typeInfo = getAssetTypeInfo(asset.type);
    const IconComponent = typeInfo.icon;
    
    // Calculate professional metrics
    const roi = calculateROI(asset);
    const depreciation = calculateDepreciation(asset);
    const insurance = getInsuranceCoverage(asset);
    const liquidityInfo = getLiquidityInfo(asset.liquidity);

    return (
      <>
        <tr key={asset._id || newAsset} className="border-b border-gray-200 hover:bg-gray-50">
        {/* Asset Type Icon & Name */}
        <td className="px-4 py-3 w-80">
          {isEditing ? (
            <div className="space-y-2">
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Asset name"
                className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <select
                value={formData.type || 'personal'}
                onChange={(e) => handleInputChange('type', e.target.value)}
                className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {ASSET_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="flex items-center space-x-3">
              <IconComponent className={`h-5 w-5 ${typeInfo.color}`} />
              <div>
                <div className="font-medium text-gray-900">{asset.name}</div>
                <div className="text-sm text-gray-500">{typeInfo.label}</div>
              </div>
            </div>
          )}
        </td>

        {/* Current Value */}
        <td className="px-4 py-3 w-32">
          {isEditing ? (
            renderEditableCell('currentValue', 'number')
          ) : (
            <span className="font-semibold text-green-600">
              {formatCurrency(asset.currentValue)}
            </span>
          )}
        </td>

        {/* Purchase Price */}
        <td className="px-4 py-3 w-32">
          {isEditing ? (
            renderEditableCell('purchasePrice', 'number')
          ) : (
            <span className="text-gray-600">
              {formatCurrency(asset.purchasePrice)}
            </span>
          )}
        </td>

        {/* Equity - Full value for regular assets (no debt) */}
        <td className="px-4 py-3 w-32">
          {isEditing ? (
            <span className="text-gray-400 text-sm">-</span>
          ) : (
            <div>
              <span className="font-semibold text-green-600">
                {formatCurrency(asset.currentValue)}
              </span>
              <div className="text-xs text-gray-500">Full Equity</div>
            </div>
          )}
        </td>

        {/* Debt - None for regular assets */}
        <td className="px-4 py-3 w-32">
          {isEditing ? (
            <span className="text-gray-400 text-sm">-</span>
          ) : (
            <div>
              <span className="font-semibold text-gray-400">
                {formatCurrency(0)}
              </span>
              <div className="text-xs text-gray-500">No Debt</div>
            </div>
          )}
        </td>

        {/* Purchase Date */}
        <td className="px-4 py-3 w-32">
          {isEditing ? (
            renderEditableCell('purchaseDate', 'date')
          ) : (
            <span className="text-gray-600">
              {asset.purchaseDate ? new Date(asset.purchaseDate).toLocaleDateString() : '-'}
            </span>
          )}
        </td>

        {/* Condition */}
        <td className="px-4 py-3 w-24">
          {isEditing ? (
            renderEditableCell('condition', 'select', CONDITION_OPTIONS)
          ) : (
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
              asset.condition === 'excellent' ? 'bg-green-100 text-green-800' :
              asset.condition === 'good' ? 'bg-blue-100 text-blue-800' :
              asset.condition === 'fair' ? 'bg-yellow-100 text-yellow-800' :
              'bg-red-100 text-red-800'
            }`}>
              {CONDITION_OPTIONS.find(c => c.value === asset.condition)?.label || 'Good'}
            </span>
          )}
        </td>

        {/* Location */}
        <td className="px-4 py-3 w-40">
          {isEditing ? (
            renderEditableCell('location', 'text')
          ) : (
            <span className="text-gray-600">{asset.location || '-'}</span>
          )}
        </td>

        {/* Shared */}
        <td className="px-4 py-3 w-20 text-center">
          {isEditing ? (
            renderEditableCell('isShared', 'checkbox')
          ) : (
            <span className={`inline-flex h-2 w-2 rounded-full ${asset.isShared ? 'bg-blue-500' : 'bg-gray-300'}`}></span>
          )}
        </td>

        {/* Analysis Report */}
        <td className="px-4 py-3 w-32 text-center">
          {!isEditing && (
            <button
              onClick={() => {
                const newExpanded = new Set(expandedRows);
                const analysisKey = `${asset._id}-analysis`;
                if (newExpanded.has(analysisKey)) {
                  newExpanded.delete(analysisKey);
                } else {
                  newExpanded.add(analysisKey);
                }
                setExpandedRows(newExpanded);
              }}
              className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded transition-colors"
            >
              <ClipboardDocumentListIcon className="h-3 w-3 mr-1" />
              Report
            </button>
          )}
        </td>

        {/* Actions */}
        <td className="px-4 py-3 w-24">
          {isEditing ? (
            <div className="flex space-x-2">
              <button
                onClick={handleSave}
                className="p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded"
                title="Save"
              >
                <CheckIcon className="h-4 w-4" />
              </button>
              <button
                onClick={handleCancel}
                className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded"
                title="Cancel"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="flex space-x-2">
              <button
                onClick={() => handleEdit(asset)}
                className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                title="Edit"
              >
                <PencilIcon className="h-4 w-4" />
              </button>
              <button
                onClick={() => handleDelete(asset._id)}
                className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded"
                title="Delete"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          )}
        </td>
      </tr>
      
      {/* Analysis Report Row */}
      {!isEditing && expandedRows.has(`${asset._id}-analysis`) && (
        <tr>
          <td colSpan="11" className="px-4 py-0 bg-purple-50 border-b border-purple-200">
            <div className="py-4">
              <h5 className="text-sm font-semibold text-purple-800 mb-3">Asset Analysis Report</h5>
              
              {/* Performance Metrics */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <div className="bg-white p-3 rounded-lg">
                  <div className="text-xs font-medium text-gray-500 mb-1">ROI</div>
                  <div className={`text-sm font-semibold ${roi >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {roi.toFixed(1)}%
                  </div>
                </div>
                <div className="bg-white p-3 rounded-lg">
                  <div className="text-xs font-medium text-gray-500 mb-1">Depreciation</div>
                  <div className="text-sm text-orange-600">
                    {formatCurrency(depreciation)}
                  </div>
                </div>
                {insurance && (
                  <div className="bg-white p-3 rounded-lg">
                    <div className="flex items-center justify-between mb-1">
                      <div className="text-xs font-medium text-gray-500">Insurance Coverage</div>
                      {!insurance.adequate && (
                        <ExclamationTriangleIcon className="h-3 w-3 text-amber-500" />
                      )}
                    </div>
                    <div className="text-sm">
                      <span className={insurance.adequate ? 'text-green-600' : 'text-red-600'}>
                        {formatCurrency(insurance.amount)} ({insurance.ratio.toFixed(0)}%)
                      </span>
                    </div>
                  </div>
                )}
                {liquidityInfo && (
                  <div className="bg-white p-3 rounded-lg">
                    <div className="text-xs font-medium text-gray-500 mb-1">Liquidity</div>
                    <div className={`text-sm ${liquidityInfo.color}`}>
                      {liquidityInfo.label.split(' ')[0]}
                    </div>
                  </div>
                )}
              </div>

              {/* Asset-Specific Analysis */}
              {asset.type === 'real_estate' && (
                <div className="bg-white p-3 rounded-lg mb-3">
                  <div className="text-xs font-medium text-gray-500 mb-2">Real Estate Analysis</div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Market Value:</span>
                      <span className="font-medium ml-2">{formatCurrency(asset.currentValue)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Price per Sq Ft:</span>
                      <span className="font-medium ml-2">
                        {asset.squareFootage ? `$${(asset.currentValue / asset.squareFootage).toFixed(0)}` : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Asset-Specific Details */}
              <div className="space-y-4">
                {/* Vehicle Details Section */}
                {asset.type === 'vehicle' && (asset.vehicleMake || asset.vehicleModel || asset.vehicleYear || asset.mileage || asset.vinNumber || asset.licensePlate) && (
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center mb-3">
                      <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <h6 className="text-sm font-semibold text-gray-800">Vehicle Details</h6>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {asset.vehicleMake && (
                        <div className="text-sm">
                          <span className="text-gray-500">Make:</span>
                          <span className="font-medium ml-2">{asset.vehicleMake}</span>
                        </div>
                      )}
                      {asset.vehicleModel && (
                        <div className="text-sm">
                          <span className="text-gray-500">Model:</span>
                          <span className="font-medium ml-2">{asset.vehicleModel}</span>
                        </div>
                      )}
                      {asset.vehicleYear && (
                        <div className="text-sm">
                          <span className="text-gray-500">Year:</span>
                          <span className="font-medium ml-2">{asset.vehicleYear}</span>
                        </div>
                      )}
                      {asset.mileage && (
                        <div className="text-sm">
                          <span className="text-gray-500">Mileage:</span>
                          <span className="font-medium ml-2">{parseInt(asset.mileage).toLocaleString()} miles</span>
                        </div>
                      )}
                      {asset.vinNumber && (
                        <div className="text-sm">
                          <span className="text-gray-500">VIN:</span>
                          <span className="font-medium ml-2 font-mono text-xs">{asset.vinNumber}</span>
                        </div>
                      )}
                      {asset.licensePlate && (
                        <div className="text-sm">
                          <span className="text-gray-500">License Plate:</span>
                          <span className="font-medium ml-2">{asset.licensePlate}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Financing Information Section */}
                {(asset.financingType || asset.loanBalance || asset.monthlyPayment || asset.interestRate || asset.downPayment || asset.loanTerm || asset.lenderName) && (
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center mb-3">
                      <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                      </div>
                      <h6 className="text-sm font-semibold text-gray-800">Financing Information</h6>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {asset.financingType && (
                        <div className="text-sm">
                          <span className="text-gray-500">Financing Type:</span>
                          <span className="font-medium ml-2 capitalize">{asset.financingType.replace('_', ' ')}</span>
                        </div>
                      )}
                      {asset.downPayment && (
                        <div className="text-sm">
                          <span className="text-gray-500">Down Payment:</span>
                          <span className="font-medium ml-2">{formatCurrency(asset.downPayment)}</span>
                        </div>
                      )}
                      {asset.loanBalance && (
                        <div className="text-sm">
                          <span className="text-gray-500">Loan Balance:</span>
                          <span className="font-medium ml-2 text-red-600">{formatCurrency(asset.loanBalance)}</span>
                        </div>
                      )}
                      {asset.monthlyPayment && (
                        <div className="text-sm">
                          <span className="text-gray-500">Monthly Payment:</span>
                          <span className="font-medium ml-2">{formatCurrency(asset.monthlyPayment)}</span>
                        </div>
                      )}
                      {asset.interestRate && (
                        <div className="text-sm">
                          <span className="text-gray-500">Interest Rate:</span>
                          <span className="font-medium ml-2">{asset.interestRate}%</span>
                        </div>
                      )}
                      {asset.loanTerm && (
                        <div className="text-sm">
                          <span className="text-gray-500">Loan Term:</span>
                          <span className="font-medium ml-2">{asset.loanTerm} months</span>
                        </div>
                      )}
                      {asset.lenderName && (
                        <div className="text-sm">
                          <span className="text-gray-500">Lender:</span>
                          <span className="font-medium ml-2">{asset.lenderName}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Professional & Tax Information Section */}
                {(asset.costBasis || asset.improvements || asset.depreciationMethod || asset.liquidity || asset.insuranceCoverage || asset.taxClassification) && (
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center mb-3">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </div>
                      <h6 className="text-sm font-semibold text-gray-800">Professional & Tax Information</h6>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {asset.costBasis && (
                        <div className="text-sm">
                          <span className="text-gray-500">Cost Basis:</span>
                          <span className="font-medium ml-2">{formatCurrency(asset.costBasis)}</span>
                        </div>
                      )}
                      {asset.improvements && (
                        <div className="text-sm">
                          <span className="text-gray-500">Improvements:</span>
                          <span className="font-medium ml-2">{formatCurrency(asset.improvements)}</span>
                        </div>
                      )}
                      {asset.depreciationMethod && asset.depreciationMethod !== 'none' && (
                        <div className="text-sm">
                          <span className="text-gray-500">Depreciation Method:</span>
                          <span className="font-medium ml-2 capitalize">{asset.depreciationMethod.replace('-', ' ')}</span>
                        </div>
                      )}
                      {asset.liquidity && (
                        <div className="text-sm">
                          <span className="text-gray-500">Liquidity:</span>
                          <span className={`font-medium ml-2 capitalize ${
                            asset.liquidity === 'high' ? 'text-green-600' : 
                            asset.liquidity === 'medium' ? 'text-yellow-600' : 'text-red-600'
                          }`}>{asset.liquidity}</span>
                        </div>
                      )}
                      {asset.insuranceCoverage && (
                        <div className="text-sm">
                          <span className="text-gray-500">Insurance Coverage:</span>
                          <span className="font-medium ml-2">{formatCurrency(asset.insuranceCoverage)}</span>
                        </div>
                      )}
                      {asset.taxClassification && (
                        <div className="text-sm">
                          <span className="text-gray-500">Tax Classification:</span>
                          <span className="font-medium ml-2 capitalize">{asset.taxClassification}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Real Estate Details Section */}
                {asset.type === 'real_estate' && (asset.propertyAddress || asset.propertyType || asset.squareFootage || asset.yearBuilt || asset.bedrooms || asset.bathrooms) && (
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center mb-3">
                      <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                        </svg>
                      </div>
                      <h6 className="text-sm font-semibold text-gray-800">Real Estate Details</h6>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {asset.propertyAddress && (
                        <div className="text-sm md:col-span-2">
                          <span className="text-gray-500">Address:</span>
                          <span className="font-medium ml-2">{asset.propertyAddress}</span>
                        </div>
                      )}
                      {asset.propertyType && (
                        <div className="text-sm">
                          <span className="text-gray-500">Property Type:</span>
                          <span className="font-medium ml-2 capitalize">{asset.propertyType.replace('_', ' ')}</span>
                        </div>
                      )}
                      {asset.squareFootage && (
                        <div className="text-sm">
                          <span className="text-gray-500">Square Footage:</span>
                          <span className="font-medium ml-2">{parseInt(asset.squareFootage).toLocaleString()} sq ft</span>
                        </div>
                      )}
                      {asset.yearBuilt && (
                        <div className="text-sm">
                          <span className="text-gray-500">Year Built:</span>
                          <span className="font-medium ml-2">{asset.yearBuilt}</span>
                        </div>
                      )}
                      {asset.bedrooms && (
                        <div className="text-sm">
                          <span className="text-gray-500">Bedrooms:</span>
                          <span className="font-medium ml-2">{asset.bedrooms}</span>
                        </div>
                      )}
                      {asset.bathrooms && (
                        <div className="text-sm">
                          <span className="text-gray-500">Bathrooms:</span>
                          <span className="font-medium ml-2">{asset.bathrooms}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Financial Analysis Section */}
                {asset.type === 'vehicle' && (
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <div className="flex items-center mb-3">
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                        <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <h6 className="text-sm font-semibold text-gray-800">Financial Analysis</h6>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="text-sm">
                        <span className="text-gray-500">Est. Annual Depreciation:</span>
                        <span className="font-medium ml-2 text-orange-600">{formatCurrency(depreciation)}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-500">Total Cost of Ownership (5yr):</span>
                        <span className="font-medium ml-2">
                          {formatCurrency((asset.purchasePrice || 0) + (depreciation * 5))}
                        </span>
                      </div>
                      {asset.vehicleYear && (
                        <div className="text-sm">
                          <span className="text-gray-500">Vehicle Age:</span>
                          <span className="font-medium ml-2">{new Date().getFullYear() - parseInt(asset.vehicleYear)} years</span>
                        </div>
                      )}
                      {asset.mileage && asset.vehicleYear && (
                        <div className="text-sm">
                          <span className="text-gray-500">Avg Miles/Year:</span>
                          <span className="font-medium ml-2">
                            {Math.round(parseInt(asset.mileage) / Math.max(1, new Date().getFullYear() - parseInt(asset.vehicleYear))).toLocaleString()}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
    );
  };

  // Safe number helper for calculations
  const safeNumber = (value) => {
    if (value === null || value === undefined || value === '') return 0;
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };

  const calculatePortfolioAnalysis = () => {
    const totalValue = calculateTotalValue();
    const assetCount = safeAssets.length + liabilityAssets.length;
    
    // Asset allocation analysis
    const allocationKeys = Object.keys(assetAllocation);
    let topCategory = 'none';
    let topCategoryPercentage = 0;
    
    if (allocationKeys.length > 0) {
      topCategory = allocationKeys.reduce((a, b) => {
        const aPercentage = safeNumber(assetAllocation[a]?.percentage);
        const bPercentage = safeNumber(assetAllocation[b]?.percentage);
        return aPercentage > bPercentage ? a : b;
      });
      topCategoryPercentage = safeNumber(assetAllocation[topCategory]?.percentage);
    }
    
    // Risk assessment
    let riskLevel = 'Low';
    if (topCategoryPercentage > 60) riskLevel = 'High';
    else if (topCategoryPercentage > 40) riskLevel = 'Medium';
    
    // Average ROI calculation
    const assetsWithROI = safeAssets.filter(asset => {
      // For real estate assets, we need both purchasePrice and currentValue (home value)
      if (asset.type === 'real_estate') {
        return asset.purchasePrice && asset.currentValue;
      }
      // For other assets, use standard logic
      return asset.purchasePrice && asset.currentValue;
    });
    
    const avgROI = assetsWithROI.length > 0 
      ? assetsWithROI.reduce((sum, asset) => {
          const purchasePrice = safeNumber(asset.purchasePrice);
          const currentValue = safeNumber(asset.currentValue);
          const improvements = safeNumber(asset.improvements);
          const totalCost = purchasePrice + improvements;
          const roi = totalCost > 0 ? ((currentValue - totalCost) / totalCost) * 100 : 0;
          const safeROI = safeNumber(roi);
          return sum + safeROI;
        }, 0) / assetsWithROI.length
      : 0;

    return {
      totalValue: safeNumber(totalValue),
      assetCount,
      topCategory: topCategory.replace('_', ' '),
      topCategoryPercentage: safeNumber(topCategoryPercentage),
      riskLevel,
      avgROI: safeNumber(avgROI)
    };
  };

  const analysis = calculatePortfolioAnalysis();

  return (
    <div className="space-y-6">
      {/* Assets Overview Header */}
      <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold mb-2">Assets Overview</h2>
            <p className="text-emerald-100">Manage your valuable assets and track their worth</p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{formatCurrency(analysis.totalValue)}</div>
            <div className="text-emerald-100">
              {analysis.assetCount} asset{analysis.assetCount !== 1 ? 's' : ''} tracked
            </div>
          </div>
        </div>
      </div>

      {/* Portfolio Analysis Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Portfolio Value */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-600 text-sm font-medium">Portfolio Value</p>
              <p className="text-2xl font-bold text-emerald-800">{formatCurrency(analysis.totalValue)}</p>
              <p className="text-emerald-600 text-xs">{analysis.assetCount} assets tracked</p>
            </div>
            <ChartBarIcon className="h-8 w-8 text-emerald-600" />
          </div>
        </div>

        {/* Asset Allocation */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-600 text-sm font-medium">Asset Allocation</p>
              <p className="text-lg font-bold text-blue-800 capitalize">{analysis.topCategory}</p>
              <p className="text-blue-600 text-xs">{(analysis.topCategoryPercentage || 0).toFixed(1)}% of portfolio</p>
            </div>
            <PlusIcon className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        {/* Risk Assessment */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-600 text-sm font-medium">Risk Level</p>
              <p className={`text-lg font-bold ${
                analysis.riskLevel === 'High' ? 'text-red-600' : 
                analysis.riskLevel === 'Medium' ? 'text-yellow-600' : 'text-green-600'
              }`}>
                {analysis.riskLevel}
              </p>
              <p className="text-amber-600 text-xs">Concentration risk</p>
            </div>
            <CubeIcon className="h-8 w-8 text-amber-600" />
          </div>
        </div>

        {/* Income Performance */}
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-600 text-sm font-medium">Avg ROI</p>
              <p className={`text-lg font-bold ${analysis.avgROI >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {(analysis.avgROI || 0).toFixed(1)}%
              </p>
              <p className="text-purple-600 text-xs">Portfolio average</p>
            </div>
            <BanknotesIcon className="h-8 w-8 text-purple-600" />
          </div>
        </div>
      </div>

      {/* Add Asset Button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold text-gray-900">Assets</h3>
        <button
          onClick={handleStartNew}
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Asset
        </button>
      </div>

      {/* Mobile View: Cards */}
      <div className="block lg:hidden">
        <div className="p-4 space-y-4">
          {/* Existing Assets */}
          {safeAssets.map(asset => renderMobileAssetCard(asset, false))}
          
          {/* Liability Assets */}
          {liabilityAssets.map(debt => renderMobileAssetCard(debt, true))}
          
          {/* Empty State */}
          {safeAssets.length === 0 && liabilityAssets.length === 0 && (
            <div className="text-center py-12">
              <CubeIcon className="h-12 w-12 text-gray-400 mb-4 mx-auto" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No assets yet</h3>
              <p className="text-gray-500 text-sm mb-6 px-4">
                Start tracking your valuable assets to get a complete picture of your net worth.
                Assets from loan accounts will automatically appear here when you add loan details.
              </p>
              <button
                onClick={handleStartNew}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Your First Asset
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Desktop View: Enhanced Cards */}
      <div className="hidden lg:block">
        <AssetsDesktopCards
          assets={safeAssets}
          liabilityAssets={liabilityAssets}
          onEdit={handleEdit}
          onDelete={handleDelete}
          expandedRows={expandedRows}
          onToggleExpanded={toggleRowExpansion}
        />
        
        {/* Empty State for Desktop */}
        {safeAssets.length === 0 && liabilityAssets.length === 0 && (
          <div className="text-center py-12">
            <CubeIcon className="h-12 w-12 text-gray-400 mb-4 mx-auto" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No assets yet</h3>
            <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
              Start tracking your valuable assets to get a complete picture of your net worth.
              Assets from loan accounts will automatically appear here when you add loan details.
            </p>
            <button
              onClick={handleStartNew}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Your First Asset
            </button>
          </div>
        )}
      </div>

      {/* Asset Management Tips */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <Card className="p-4 border border-blue-200 bg-blue-50">
          <div className="flex items-start space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <CubeIcon className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h4 className="font-semibold text-blue-900 mb-2">Asset Management Tips</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Update current values regularly to maintain accurate net worth calculations</li>
                <li>• Use the "Shared" option for assets owned jointly with your partner</li>
                <li>• Consider depreciation for vehicles and electronics over time</li>
                <li>• Keep purchase receipts and appraisal documents for valuable items</li>
                <li>• Loan assets automatically appear when you add asset categories to loans in your Wallet</li>
              </ul>
            </div>
          </div>
        </Card>

        <Card className="p-4 border border-purple-200 bg-purple-50">
          <div className="flex items-start space-x-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <ChartBarIcon className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h4 className="font-semibold text-purple-900 mb-2">Professional Tips</h4>
              <ul className="text-sm text-purple-800 space-y-1">
                <li>• Track cost basis and improvements for accurate tax calculations</li>
                <li>• Review insurance coverage annually - aim for 80% replacement value</li>
                <li>• Diversify asset allocation to reduce concentration risk</li>
                <li>• Consider depreciation methods for business and rental assets</li>
                <li>• Schedule professional appraisals for high-value items every 3-5 years</li>
              </ul>
            </div>
          </div>
        </Card>
      </div>

      {/* Asset Creation Modal */}
      <AssetCreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSave={handleModalSave}
        loading={isCreating}
      />

      {/* Asset Edit Modal */}
      <AssetCreateModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setEditingAsset(null);
        }}
        onSave={handleModalSave}
        loading={isCreating}
        editingAsset={editingAsset}
      />
    </div>
  );
};

export default AssetsSection;